import React, { useState } from "react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Plus, Eye, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import {
  DUMMY_SLOT_MACHINES,
  SlotMachineConfig,
} from "./dummyData";

export function GamificationAllSlotMachines() {
  const [slotMachines] = useState<SlotMachineConfig[]>(DUMMY_SLOT_MACHINES);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedSlotMachine, setSelectedSlotMachine] =
    useState<SlotMachineConfig | null>(null);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  const handleView = (machine: SlotMachineConfig) => {
    setSelectedSlotMachine(machine);
    setIsViewModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-800">
            All Slot Machines
          </h2>
          <p className="text-sm text-muted-foreground">
            View and manage all slot machine configurations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href="/slot-machine" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              Public Page
            </a>
          </Button>
          <Button
            type="button"
            className="flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700"
          >
            <Plus className="h-4 w-4" />
            Create Slot Machine
          </Button>
        </div>
      </div>

      <DataTable
        title=""
        data={slotMachines}
        cellClassName="py-6 text-gray-700"
        columns={[
          {
            header: "Name",
            accessorKey: "name" as keyof SlotMachineConfig,
          },
          {
            header: "Start Date",
            accessorKey: "startDate" as keyof SlotMachineConfig,
            cell: (row: SlotMachineConfig) => formatDate(row.startDate),
          },
          {
            header: "End Date",
            accessorKey: "endDate" as keyof SlotMachineConfig,
            cell: (row: SlotMachineConfig) => formatDate(row.endDate),
          },
          {
            header: "Spins per Member",
            accessorKey: "spinsPerMember" as keyof SlotMachineConfig,
            cell: (row: SlotMachineConfig) => `${row.spinsPerMember} per day`,
          },
          {
            header: "Bonus Validity (Days)",
            accessorKey: "bonusValidityInDay" as keyof SlotMachineConfig,
            cell: (row: SlotMachineConfig) => `${row.bonusValidityInDay} days`,
          },
          {
            header: "Symbols",
            id: "symbols",
            cell: (row: SlotMachineConfig) => row.symbols.length.toString(),
          },
          {
            header: "Status",
            accessorKey: "isActive" as keyof SlotMachineConfig,
            cell: (row: SlotMachineConfig) => (
              <Badge
                variant={row.isActive ? "default" : "secondary"}
                className="flex items-center gap-1 w-fit"
              >
                {row.isActive ? "Active" : "Inactive"}
              </Badge>
            ),
          },
          {
            header: "Action",
            id: "action",
            cell: (row: SlotMachineConfig) => (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleView(row)}
                className="flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                View
              </Button>
            ),
          },
        ]}
      />

      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Slot Machine Details</DialogTitle>
          </DialogHeader>
          {selectedSlotMachine && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">ID</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSlotMachine.id}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Name
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSlotMachine.name}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Start Date
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedSlotMachine.startDate)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    End Date
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedSlotMachine.endDate)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Spins per Member
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSlotMachine.spinsPerMember} per day
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Bonus Validity (Days)
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSlotMachine.bonusValidityInDay} days
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    <Badge
                      variant={
                        selectedSlotMachine.isActive ? "default" : "secondary"
                      }
                    >
                      {selectedSlotMachine.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-500 mb-2 block">
                  Symbol Details
                </label>
                <div className="space-y-2">
                  {selectedSlotMachine.symbols.map((symbol) => (
                    <div key={symbol.id} className="p-3 border rounded-md">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">
                          {symbol.emoji} {symbol.name}
                        </span>
                        <Badge variant="default" className="ml-2">
                          {symbol.points} points
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
